import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

// 2026 Trend-focused image assets
const livingRoomImages = {
  hero: "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&w=1200&q=80",
  layered: "https://images.unsplash.com/photo-1513694203232-719a280e022f?auto=format&fit=crop&w=600&q=80",
  wooden: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=600&q=80",
};

function LivingRoomBlinds() {
  return (
    <div className="max-w-7xl mx-auto px-6 py-12 text-zinc-900">
      <Helmet>
        <title>Living Room Blinds UK | Modern Designer Window Blinds 2026</title>
        <meta
          name="description"
          content="Elevate your lounge with premium living room blinds. Discover 2026's top trends: Real wood venetians, elegant Roman blinds, and light-filtering rollers at MF Blinds."
        />
        <meta name="keywords" content="Living Room Blinds UK, Lounge Window Coverings, Modern Roman Blinds, Wooden Venetian Blinds, Light Filtering Blinds, Designer Living Room Decor" />
      </Helmet>

      {/* --- Section 1: Hero & Design Philosophy --- */}
      <header className="grid lg:grid-cols-2 gap-16 items-center mb-24">
        <div>
          <span className="text-rose-600 font-bold tracking-widest uppercase text-xs mb-2 block">Premium Lounge Collection</span>
          <h1 className="text-5xl md:text-7xl font-extrabold mb-6 leading-tight tracking-tighter">
            The Heart <br />Of the <span className="text-rose-600 italic">Home.</span>
          </h1>
          <p className="text-xl text-zinc-600 mb-8 leading-relaxed">
            Your living room is where style meets comfort. Our <strong>designer living room blinds</strong> are curated to balance the need for natural sunlight with the essential privacy your lounge deserves.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <button className="bg-zinc-900 text-white px-10 py-4 rounded-full font-bold hover:bg-rose-600 transition-all shadow-xl">
              Shop Designer Styles
            </button>
            <button className="bg-transparent border-2 border-zinc-200 px-10 py-4 rounded-full font-bold hover:bg-zinc-50 transition">
              View Lookbook
            </button>
          </div>
        </div>
        <div className="relative">
          <img 
            src={livingRoomImages.hero} 
            alt="Minimalist living room with neutral Roman blinds" 
            className="rounded-[3rem] shadow-2xl h-[550px] w-full object-cover"
          />
        </div>
      </header>

      {/* --- Section 2: 2026 Trends (Viral Content) --- */}
      <section className="mb-24">
        <h2 className="text-3xl font-bold mb-12 text-center">Trending for 2026</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="relative group overflow-hidden rounded-3xl">
            <img src={livingRoomImages.layered} alt="Layered blinds and curtains" className="w-full h-80 object-cover group-hover:scale-105 transition duration-700" />
            <div className="absolute inset-0 bg-black/40 flex flex-col justify-end p-8 text-white">
              <h3 className="text-2xl font-bold mb-2">Layered Aesthetics</h3>
              <p className="text-sm opacity-90 mb-4">Combine sheer rollers with heavy Roman blinds for a multi-dimensional, high-end look.</p>
              <Link to="/roman-blinds" className="text-sm font-bold underline">Explore Roman Range</Link>
            </div>
          </div>
          <div className="relative group overflow-hidden rounded-3xl">
            <img src={livingRoomImages.wooden} alt="Natural wood blinds" className="w-full h-80 object-cover group-hover:scale-105 transition duration-700" />
            <div className="absolute inset-0 bg-black/40 flex flex-col justify-end p-8 text-white">
              <h3 className="text-2xl font-bold mb-2">Biophilic Wood</h3>
              <p className="text-sm opacity-90 mb-4">Bring the outdoors in with sustainable, real-wood Venetian blinds that add warmth to modern spaces.</p>
              <Link to="/wooden-blinds" className="text-sm font-bold underline">Explore Wooden Range</Link>
            </div>
          </div>
        </div>
      </section>

      {/* --- Section 3: Value Propositions --- */}
      <div className="bg-zinc-50 rounded-[4rem] px-10 py-20 mb-24 grid md:grid-cols-3 gap-12 border border-zinc-100">
        <div className="text-center">
          <div className="text-3xl mb-4">🔆</div>
          <h4 className="text-lg font-bold mb-2">Glare Reduction</h4>
          <p className="text-zinc-600 text-sm">Perfect for TV rooms and home offices; block reflections without losing natural light.</p>
        </div>
        <div className="text-center">
          <div className="text-3xl mb-4">🛡️</div>
          <h4 className="text-lg font-bold mb-2">UV Protection</h4>
          <p className="text-zinc-600 text-sm">Protect your expensive furniture and flooring from sun-fading with UV-rated fabrics.</p>
        </div>
        <div className="text-center">
          <div className="text-3xl mb-4">🎨</div>
          <h4 className="text-lg font-bold mb-2">Custom Finishes</h4>
          <p className="text-zinc-600 text-sm">Over 500+ textures and colors to ensure a perfect match with your existing lounge palette.</p>
        </div>
      </div>

      {/* --- Section 4: Deep-Dive SEO Content --- */}
      <article className="prose lg:prose-xl max-w-none mb-24 text-zinc-800">
        <h2 className="text-3xl font-bold mb-6">Choosing the Perfect Living Room Blinds</h2>
        <p>
          Unlike a bedroom or bathroom, the living room window serves multiple purposes throughout the day. In the morning, you want diffused light; in the evening, you require absolute privacy. This is why <strong>Vertical Blinds</strong> and <strong>Venetian Blinds</strong> remain the gold standard—they allow you to tilt the slats to find the perfect balance.
        </p>
        <p className="mt-4">
          If you prefer a softer, more fabric-focused vibe, our <strong>Luxury Roman Blinds</strong> offer a timeless elegance. For large patio or bi-fold doors, consider our <Link to="/perfect-fit-blinds" className="text-rose-600 underline">Perfect Fit Blinds</Link> which move with the glass, keeping your doorway clear and unobstructed.
        </p>
      </article>

      {/* --- Section 5: Dynamic FAQ --- */}
      <section className="mb-24">
        <h2 className="text-3xl font-bold mb-10">Living Room Style Guide FAQ</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="p-8 bg-white border border-zinc-200 rounded-2xl">
            <h5 className="font-bold text-lg mb-2">Should living room blinds match the walls?</h5>
            <p className="text-zinc-600">Matching the blinds to your walls creates a sense of space and calm. However, choosing a contrasting texture—like a dark wood against a light wall—can create a stunning focal point.</p>
          </div>
          <div className="p-8 bg-white border border-zinc-200 rounded-2xl">
            <h5 className="font-bold text-lg mb-2">Are sheer blinds private enough?</h5>
            <p className="text-zinc-600">Sheer or "Voile" blinds are great for daytime privacy (people can't see in), but at night when the lights are on, they become transparent. We recommend layering them with a blackout roller.</p>
          </div>
        </div>
      </section>

      {/* --- Section 6: Internal Navigation & CTA --- */}
      <footer className="bg-zinc-900 text-white p-12 md:p-20 rounded-[3rem] text-center">
        <h2 className="text-4xl font-bold mb-6">Ready to Elevate Your Lounge?</h2>
        <p className="text-zinc-400 mb-10 max-w-xl mx-auto">From modern minimalist to cozy traditional, find the window treatment that defines your style.</p>
        
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          <Link to="/vertical-blinds" className="px-6 py-2 border border-zinc-700 rounded-full hover:bg-zinc-800 transition">Vertical Blinds</Link>
          <Link to="/wooden-blinds" className="px-6 py-2 border border-zinc-700 rounded-full hover:bg-zinc-800 transition">Wooden Blinds</Link>
          <Link to="/perfect-fit-blinds" className="px-6 py-2 border border-zinc-700 rounded-full hover:bg-zinc-800 transition">Perfect Fit</Link>
        </div>

        <button className="bg-rose-600 text-white px-12 py-5 rounded-full text-xl font-bold shadow-2xl hover:scale-105 transition-transform">
          Order Free Samples Now
        </button>
      </footer>
    </div>
  );
}

export default LivingRoomBlinds;